import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Collapse } from 'src/design/atoms/Collapse';
import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Table } from 'src/design/molecules/Table';
import { Textarea } from 'src/design/atoms/Textarea';
import { TextInput } from 'src/design/atoms/TextInput';
import { useDisclosure } from '@mantine/hooks';
import React from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { isApiErrorStatus } from 'src/api/utils/errorUtils';
import {
  formatHours,
  formatCurrency,
  formatDate,
  formatDateTime,
} from 'src/utils';
import { ISignaturePad, loadStoredSignature } from 'src/utils/storedSignature';
import { getWorkWeekRange } from 'src/utils/workWeek';

import {
  BatchTimesheetItemDto,
  BulkBatchApprovalRequest,
  PayCodeDto,
} from 'src/api/FiretrolApiClient';
import {
  useExternalBatchReview,
  useUpdateExternalBatchReview,
} from 'src/api/queries/timesheets/useExternalTimesheetReview';
import { ChargeCodeCell } from 'src/app/timesheets/ChargeCodeCell';
import { EffectiveRateCell } from 'src/app/timesheets/EffectiveRateCell';
import { PayCodeHoursSummary } from 'src/app/timesheets/PayCodeHoursSummary';
import { ScaleBadge } from 'src/app/timesheets/ScaleBadge';
import { useTimesheetCalculations } from 'src/hooks/useTimesheetCalculations';
import { useWindowSize } from 'src/hooks/useWindowSize';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { EXPIRED_LINK_MESSAGE } from './expiredLinkMessage';

export const CombinedTimesheetReview: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const {
    data: batch,
    isLoading: loading,
    error: loadError,
  } = useExternalBatchReview(token);
  const updateMutation = useUpdateExternalBatchReview(token ?? '');
  const submitting = updateMutation.isPending;
  const payCodes: PayCodeDto[] = batch?.payCodes ?? [];
  const error = loadError
    ? isApiErrorStatus(loadError, 401)
      ? EXPIRED_LINK_MESSAGE
      : 'Failed to load timesheet review.'
    : null;
  const [updateError, setUpdateError] = React.useState<string | null>(null);
  const [submitted, setSubmitted] = React.useState(false);
  const [approvalData, setApprovalData] = React.useState<{
    signedBy: string;
    signedOn: Date;
  } | null>(null);

  // Approval modal
  const [
    approveModalOpen,
    { open: openApproveModal, close: closeApproveModal },
  ] = useDisclosure(false);
  const [signedBy, setSignedBy] = React.useState('');
  const [signature, setSignature] = React.useState<any[]>([]);
  const signatureRef = React.useRef<any>(null);
  const [sigWidth, setSigWidth] = React.useState(500);
  const widthRef = React.useRef<HTMLDivElement>(null);
  const size = useWindowSize();

  // Rejection modal
  const [rejectModalOpen, { open: openRejectModal, close: closeRejectModal }] =
    useDisclosure(false);
  const [rejectTimesheetId, setRejectTimesheetId] = React.useState<
    string | null
  >(null);
  const [justification, setJustification] = React.useState('');

  // Reference section
  const [referenceExpanded, setReferenceExpanded] = React.useState(false);

  React.useLayoutEffect(() => {
    updateSigSize();
  }, [widthRef.current]);

  React.useEffect(() => {
    updateSigSize();
  }, [approveModalOpen, widthRef.current, size]);

  const updateSigSize = () => {
    setTimeout(() => {
      if (widthRef.current) {
        setSigWidth(widthRef.current.offsetWidth - 5);
      }
    }, 200);
  };

  const submitBatchAction = async (
    request: BulkBatchApprovalRequest,
    failureMessage: string,
  ) => {
    setUpdateError(null);
    try {
      await updateMutation.mutateAsync(request);
      return true;
    } catch (err) {
      setUpdateError(
        isApiErrorStatus(err, 401) ? EXPIRED_LINK_MESSAGE : failureMessage,
      );
      return false;
    }
  };

  const handleApproveAll = async () => {
    const ok = await submitBatchAction(
      {
        action: 'approved',
        signedBy: signedBy.trim(),
        signature: JSON.stringify(signature),
      },
      'Failed to approve timesheets. Please try again.',
    );
    if (!ok) return;
    setApprovalData({ signedBy: signedBy.trim(), signedOn: new Date() });
    setSubmitted(true);
    closeApproveModal();
  };

  const handleReject = async () => {
    const ok = await submitBatchAction(
      {
        action: 'rejected',
        justification: justification.trim(),
        rejectTimesheetIds: rejectTimesheetId ? [rejectTimesheetId] : undefined,
      },
      'Failed to reject timesheet. Please try again.',
    );
    if (!ok) return;
    closeRejectModal();
    setJustification('');
    setRejectTimesheetId(null);
  };

  const openRejectForTimesheet = (timesheetId: string) => {
    setRejectTimesheetId(timesheetId);
    setUpdateError(null);
    openRejectModal();
  };

  if (loading) {
    return (
      <Box ta="center" mt="xl">
        <LoadingOverlay visible={true} />
      </Box>
    );
  }

  if (error) {
    return <Text c="red">Error: {error}</Text>;
  }

  if (!batch) return null;

  // Week label matching original format
  const { start: weekStart, end: weekEnd } = getWorkWeekRange(
    batch.weekId,
    batch.year,
  );
  const weekLabel = `Week ${batch.weekId}: ${formatDate(
    weekStart,
    'ddd (M/D/YY)',
  )} - ${formatDate(weekEnd, 'ddd (M/D/YY)')}`;

  const user = batch.user;
  const firstTimesheet = batch.actionRequiredTimesheets?.[0]?.fullTimesheet;

  // Check if any timesheets are still pending review (in a review step)
  const timesheetsPendingReview = (batch.actionRequiredTimesheets || []).filter(
    (item) => {
      const ts = item.fullTimesheet;
      if (!ts?.workflow?.steps || ts.stepIndex == null) return false;
      const currentStep = ts.workflow.steps[ts.stepIndex];
      return (
        currentStep?.isEmployeeReviewStep || currentStep?.isSupervisorReviewStep
      );
    },
  );
  const hasTimesheetsPendingReview = timesheetsPendingReview.length > 0;

  // Email link for contacting supervisor
  const formattedBody = `\n \n${user?.fullName} Timesheet - Week ${batch.weekId}`;
  const mailToLink = `mailto:${firstTimesheet?.createdBy?.email}?subject=${
    user?.fullName
  } Timesheet Issue - Week ${batch.weekId}&body=${encodeURIComponent(
    formattedBody,
  )}`;

  return (
    <Box p="xl" mt="xl">
      <Title order={2} ta="center">
        {weekLabel}
      </Title>

      <Stack gap="md">
        {/* Total Hours Summary */}
        <Group justify="center" gap="lg">
          <Text size="lg" fw={500} c="black">
            Total Hours: {formatHours(batch.totalHours)}
          </Text>
        </Group>

        {/* Employee Info Section */}
        <Group justify="space-between" wrap="wrap">
          <Text c="black">Employee: {user?.fullName}</Text>
          <Text c="black">Employee Number: {user?.employeeId ?? 'N/A'}</Text>
          <Text c="black">
            Supervisor: {firstTimesheet?.user?.supervisor?.fullName ?? 'N/A'}
          </Text>
        </Group>

        {/* Action Required Timesheets - Always show full details */}
        {batch.actionRequiredTimesheets &&
          batch.actionRequiredTimesheets.length > 0 && (
            <Box>
              <Title order={4} mb="sm">
                Timesheets Requiring Action (
                {batch.actionRequiredTimesheets.length})
              </Title>
              {batch.actionRequiredTimesheets.map((item) => (
                <TimesheetDetailCard
                  key={item.id}
                  item={item}
                  payCodes={payCodes}
                  onReject={() => openRejectForTimesheet(item.id)}
                  showActions={true}
                />
              ))}
            </Box>
          )}

        {/* Reference Timesheets - Collapsible */}
        {batch.referenceTimesheets && batch.referenceTimesheets.length > 0 && (
          <Box>
            <BasicButton
              variant="subtle"
              onClick={() => setReferenceExpanded(!referenceExpanded)}
              mb="sm"
            >
              {referenceExpanded ? 'Hide' : 'Show'} Other Timesheets This Week (
              {batch.referenceTimesheets.length})
            </BasicButton>
            <Collapse in={referenceExpanded}>
              {batch.referenceTimesheets.map((item) => (
                <TimesheetDetailCard
                  key={item.id}
                  item={item}
                  payCodes={payCodes}
                  showActions={false}
                />
              ))}
            </Collapse>
          </Box>
        )}

        {/* Post-Approval Signature Display */}
        {submitted && approvalData && (
          <Stack gap="xs" mt="md">
            <Alert color="green" title="Timesheets Approved">
              All timesheets have been approved and advanced to the next step.
            </Alert>
            <Text size="sm">
              Have an issue with this timesheet?{' '}
              <Text
                component="a"
                href={mailToLink}
                target="_blank"
                rel="noreferrer"
                c="blue"
              >
                Email Supervisor
              </Text>
            </Text>
          </Stack>
        )}
      </Stack>

      {/* Approve Modal */}
      <BaseModal
        opened={approveModalOpen}
        onClose={closeApproveModal}
        title="Are you sure?"
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm">
            By signing these timesheets, you are certifying that the hours
            charged were incurred on the charge number and date recorded in
            accordance with company policy. Falsification of timesheets may
            result in disciplinary action up to and including termination.
          </Text>
          <Box ref={widthRef} style={{ width: '100%' }} />
          <TextInput
            label="Full Name"
            value={signedBy}
            onChange={(e) => setSignedBy(e.target.value)}
            placeholder="Enter name..."
          />
          <Text size="sm" fw={500}>
            Signature
          </Text>
          <SignatureCanvas
            ref={signatureRef}
            penColor="black"
            onEnd={() => {
              if (signatureRef?.current) {
                setSignature(signatureRef.current.toData());
              }
            }}
            canvasProps={{
              width: sigWidth,
              height: 120,
              className: 'sigCanvas',
              style: {
                border: '1px solid #d8d8d9',
                display: 'block',
              },
            }}
          />
          {updateError && (
            <Text c="red" ta="center">
              {updateError}
            </Text>
          )}
          <Group justify="flex-end" gap="sm">
            <SecondaryButton onClick={closeApproveModal}>
              Cancel
            </SecondaryButton>
            <BasicButton
              disabled={signedBy.length === 0 || signature.length === 0}
              onClick={handleApproveAll}
              loading={submitting}
            >
              Approve Timesheets
            </BasicButton>
          </Group>
        </Stack>
      </BaseModal>

      {/* Reject Modal */}
      <BaseModal
        opened={rejectModalOpen}
        onClose={closeRejectModal}
        title="Reject Timesheet with Justification"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Enter justification below and it will be routed back to the
            appropriate supervisor.
          </Text>
          <Textarea
            label="Enter justification..."
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Enter justification..."
            rows={4}
          />
          {updateError && (
            <Text c="red" ta="center">
              {updateError}
            </Text>
          )}
          <Group justify="flex-end" gap="sm">
            <SecondaryButton onClick={closeRejectModal}>Cancel</SecondaryButton>
            <BasicButton
              color="red"
              disabled={justification.length === 0 || submitting}
              onClick={handleReject}
              loading={submitting}
            >
              Reject Timesheet
            </BasicButton>
          </Group>
        </Stack>
      </BaseModal>

      {/* Action Buttons - only show if timesheets are still in a review step */}
      {hasTimesheetsPendingReview && (
        <Group justify="center" gap="md" mt="lg">
          <BasicButton color="red" onClick={() => openRejectForTimesheet('')}>
            Reject All
          </BasicButton>
          <BasicButton
            disabled={submitting}
            onClick={openApproveModal}
            loading={submitting}
          >
            Approve All
          </BasicButton>
        </Group>
      )}
    </Box>
  );
};

// Timesheet detail card - always shows full breakdown like original
interface TimesheetDetailCardProps {
  item: BatchTimesheetItemDto;
  payCodes: PayCodeDto[];
  onReject?: () => void;
  showActions?: boolean;
}

const TimesheetDetailCard: React.FC<TimesheetDetailCardProps> = ({
  item,
  payCodes,
  onReject,
  showActions = false,
}) => {
  const timesheet = item.fullTimesheet;
  const {
    totalHours,
    hoursByPayCode,
    totalsByDay,
    perDiemEligibleTotalsByDay,
    getPayCodeName,
  } = useTimesheetCalculations(timesheet?.rows || [], payCodes);

  const isServiceJob = timesheet?.job?.jobType === 'service';
  const perDiemAmount = Number(timesheet?.job?.perDiemAmount ?? 0);

  // Check if timesheet is still in a review step
  const currentStep =
    timesheet?.workflow?.steps && timesheet.stepIndex != null
      ? timesheet.workflow.steps[timesheet.stepIndex]
      : null;
  const isInReviewStep =
    currentStep?.isEmployeeReviewStep || currentStep?.isSupervisorReviewStep;

  // Calculate per diem for each day (only for hours on eligible accounts)
  const perDiemSat = perDiemEligibleTotalsByDay.sat > 0 ? perDiemAmount : 0;
  const perDiemSun = perDiemEligibleTotalsByDay.sun > 0 ? perDiemAmount : 0;
  const perDiemMon = perDiemEligibleTotalsByDay.mon > 0 ? perDiemAmount : 0;
  const perDiemTue = perDiemEligibleTotalsByDay.tue > 0 ? perDiemAmount : 0;
  const perDiemWed = perDiemEligibleTotalsByDay.wed > 0 ? perDiemAmount : 0;
  const perDiemThu = perDiemEligibleTotalsByDay.thu > 0 ? perDiemAmount : 0;
  const perDiemFri = perDiemEligibleTotalsByDay.fri > 0 ? perDiemAmount : 0;
  const perDiemTotal =
    perDiemSat +
    perDiemSun +
    perDiemMon +
    perDiemTue +
    perDiemWed +
    perDiemThu +
    perDiemFri;

  return (
    <Paper withBorder p="md" mb="md">
      {/* Job Header */}
      <Group justify="space-between" mb="md">
        <Box>
          <Title order={4}>
            {item.jobType === 'ctc'
              ? `${item.jobNumber || 'N/A'} - ${item.jobName || 'Unknown Job'}`
              : `${item.jobName || 'Unknown Job'}`}
          </Title>
          <Text size="sm" c="dimmed">
            {item.status} • {formatHours(item.totalHours)} hours
          </Text>
        </Box>
        <Group gap="xs">
          {timesheet?.job?.scalePayEnabled && <ScaleBadge isScale size="sm" />}
          {showActions && onReject && isInReviewStep && (
            <BasicButton
              size="xs"
              color="red"
              variant="outline"
              onClick={onReject}
            >
              Reject
            </BasicButton>
          )}
        </Group>
      </Group>

      {/* Full detail view - always visible */}
      {timesheet && (
        <Stack gap="md">
          {/* Pay Code Summary */}
          <PayCodeHoursSummary hoursByPayCode={hoursByPayCode} />

          {/* Status and Pay Type */}
          <Group justify="space-between" wrap="wrap">
            <Text c="black" size="sm">
              Status: {timesheet.status}
            </Text>
            <Group gap="xs">
              <Text c="black" size="sm">
                Pay Type:
              </Text>
              {timesheet.job?.scalePayEnabled ? (
                <ScaleBadge isScale size="sm" />
              ) : (
                <Text c="black" size="sm">
                  Standard
                </Text>
              )}
            </Group>
          </Group>

          {/* Full timesheet table */}
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  {isServiceJob ? 'Service Description' : 'Cost Code'}
                </Table.Th>
                <Table.Th ta="center">Pay Code</Table.Th>
                <Table.Th ta="center">Rate</Table.Th>
                <Table.Th ta="center">Sat</Table.Th>
                <Table.Th ta="center">Sun</Table.Th>
                <Table.Th ta="center">Mon</Table.Th>
                <Table.Th ta="center">Tue</Table.Th>
                <Table.Th ta="center">Wed</Table.Th>
                <Table.Th ta="center">Thu</Table.Th>
                <Table.Th ta="center">Fri</Table.Th>
                <Table.Th ta="center">Total</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {timesheet.rows?.map((row: any) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <ChargeCodeCell
                      isServiceJob={isServiceJob}
                      serviceCallId={row.serviceCallId}
                      serviceDescription={row.serviceDescription}
                      serviceLocation={row.serviceLocation}
                      fallback={row.wbsAccount?.name}
                    />
                  </Table.Td>
                  <Table.Td ta="center">
                    {getPayCodeName(row.payCodeId)}
                  </Table.Td>
                  <Table.Td ta="center">
                    <Group gap="xs" justify="center" wrap="nowrap">
                      <EffectiveRateCell
                        effectiveRate={row.effectiveRate}
                        instructions={row.breakdownInstructions}
                        loading={false}
                        error={null}
                        isActualized={!!row.breakdownInstructions}
                        payCodeColor={row.payCodeColor}
                      />
                      <ScaleBadge
                        isScale={row.isScalePay}
                        bypassScalePay={
                          row.bypassScalePay || row.payCode?.bypassScalePay
                        }
                        isDirect={row.payCode?.isDirect}
                        scalePayAdjustmentAmount={row.scalePayAdjustmentAmount}
                      />
                    </Group>
                  </Table.Td>
                  <Table.Td ta="center">{formatHours(row.sat)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.sun)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.mon)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.tue)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.wed)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.thu)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.fri)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.total)}</Table.Td>
                </Table.Tr>
              ))}
              {timesheet.rows && timesheet.rows.length > 0 && (
                <Table.Tr>
                  <Table.Td colSpan={3} fw={600}>
                    Totals
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.sat)}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.sun)}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.mon)}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.tue)}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.wed)}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.thu)}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.fri)}
                  </Table.Td>
                  <Table.Td ta="center">{formatHours(totalHours)}</Table.Td>
                </Table.Tr>
              )}
              {timesheet.job?.perDiemEnabled === true && (
                <Table.Tr>
                  <Table.Td colSpan={3} fw={600}>
                    Per Diem
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemSat, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemSun, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemMon, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemTue, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemWed, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemThu, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemFri, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemTotal, { precise: true })}
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>

          {/* Rejection info if present */}
          {timesheet.isRejected && (
            <Alert color="red" title="Timesheet Rejected">
              Justification: {timesheet.rejectJustification}
            </Alert>
          )}

          {/* Signature display if present */}
          {timesheet.signatureString &&
            timesheet.signatureString.length > 0 &&
            timesheet.signatureString !== '[]' && (
              <TimesheetSignatureDisplay timesheet={timesheet} />
            )}
        </Stack>
      )}
    </Paper>
  );
};

// Component to display the signature for a timesheet
interface TimesheetSignatureDisplayProps {
  timesheet: any;
}

const TimesheetSignatureDisplay: React.FC<TimesheetSignatureDisplayProps> = ({
  timesheet,
}) => {
  const signatureRef = React.useRef<ISignaturePad>(null);

  React.useEffect(() => {
    loadStoredSignature(signatureRef.current, timesheet.signatureString);
  }, [timesheet.signatureString]);

  return (
    <Stack gap="xs" mt="md">
      <Text size="sm">
        Signed by{' '}
        <Text component="span" fw={600}>
          {timesheet.signedBy}
        </Text>{' '}
        on {formatDateTime(timesheet.signedOn)}
      </Text>
      <Box
        style={{
          border: '1px solid #d8d8d9',
          borderRadius: 4,
          backgroundColor: '#fafafa',
        }}
      >
        <SignatureCanvas
          ref={signatureRef}
          penColor="black"
          canvasProps={{
            width: 400,
            height: 100,
            className: 'sigCanvas',
            style: {
              display: 'block',
              pointerEvents: 'none',
            },
          }}
        />
      </Box>
    </Stack>
  );
};

export default CombinedTimesheetReview;
